import { trackEvent } from '@/lib/track-event';
import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ZodType } from 'zod';

export interface ImportCsvMutationConfig<TInput, TResult> {
  path: string;
  schema: ZodType<TResult>;
  event: string;
  /** Query roots the import can change — always `queryKeys.<root>.all()`. */
  invalidate: (result: TResult) => readonly (readonly unknown[])[];
  /** Toast fragments such as "3 added"; empty means a bare "Import complete". */
  summarize: (result: TResult) => string[];
  track: (result: TResult) => Record<string, unknown>;
  onSuccess?: (result: TResult) => void;
  onError?: (error: Error) => void;
  /** Only to give TInput an inference site. */
  input?: TInput;
}

/** The mutation every spreadsheet import shares: post, track, invalidate, toast. */
export function useImportCsvMutation<TInput, TResult>(
  config: ImportCsvMutationConfig<TInput, TResult>
) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: TInput) =>
      apiClient.post<TResult>(config.path, input, { schema: config.schema }),
    onSuccess: async (result) => {
      trackEvent(config.event, config.track(result));
      await Promise.all(
        config
          .invalidate(result)
          .map((queryKey) => queryClient.invalidateQueries({ queryKey }))
      );
      const parts = config.summarize(result);
      toast.success(
        parts.length > 0
          ? `Import complete: ${parts.join(', ')}`
          : 'Import complete'
      );
      config.onSuccess?.(result);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to import the file');
      config.onError?.(error);
    },
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isImporting: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
}

/** The "n added, m updated, k already existed, e failed" fragments most kinds share. */
export function catalogueSummary(result: {
  imported: number;
  updated: number;
  skipped: number;
  errors: unknown[];
}): string[] {
  const parts: string[] = [];
  if (result.imported > 0) parts.push(`${result.imported} added`);
  if (result.updated > 0) parts.push(`${result.updated} updated`);
  if (result.skipped > 0) parts.push(`${result.skipped} already existed`);
  if (result.errors.length > 0) parts.push(`${result.errors.length} failed`);
  return parts;
}
